import { ReactElement, Suspense, useEffect, useMemo, useState } from "react";
import { BaseEntryResultType, FilterValues, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { isSearchFilterValues } from "@kaltura/mediaspace-shared-utils";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { SearchResultsDisplayComponentProps } from "./types";
import { useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";

export interface SearchResultsContainerProps<SearchResultT, PropsT = never> {
    searchUrl: string;
    filterValues?: FilterValues;
    pageSize: number;
    onEntryShowMore: (searchResult: BaseEntryResultType, index?: number) => void;

    /**
     * does the component have full page width or explore-bar open
     */
    isWideLayout: boolean;
    /**
     * search-in label (20 results found in `Details`)
     */
    searchInLabel?: string;

    /**
     * component that displays actual results
     */
    searchResultsDisplayComponent: (props: SearchResultsDisplayComponentProps<SearchResultT> & PropsT) => ReactElement;

    /**
     * component to display while loading
     */
    searchResultsSkeletonComponent: () => ReactElement;

    /**
     * bulk button actions list
     */
    bulkButtonActions?: KmsTypeComponent[];

    /**
     * bulk menu actions list
     */
    bulkMenuActions?: KmsTypeComponent[];

    displayComponentProps?: PropsT;

    /**
     * boolean flag to indicate the search results items are draggable
     */
    areSearchResultsDraggable?: boolean;
}

/**
 * Global Search Results Container - fetch search results from kms
 */
export function SearchResultsContainer<SearchResultT, PropsT>(
    props: SearchResultsContainerProps<SearchResultT, PropsT>
) {
    const {
        searchUrl,
        filterValues,
        pageSize,
        onEntryShowMore,
        isWideLayout,
        searchInLabel,
        searchResultsDisplayComponent: SearchResultsDisplayComponent,
        searchResultsSkeletonComponent: SearchResultsSkeletonComponent,
        bulkButtonActions,
        bulkMenuActions,
        displayComponentProps,
        areSearchResultsDraggable = false,
    } = props;
    const [page, setPage] = useState(1);
    const [invalidateCount, setInvalidateCount] = useState(0);

    const requestParams = useMemo(() => ({ ...filterValues, page, pageSize }), [filterValues, page, pageSize]);
    const fetchModuleData = useFetchKmsData<{ data: SearchResultT }>(
        { url: searchUrl, data: requestParams, abortable: true },
        // `invalidateCount` was added to dependencies to reload the data when it changes
        [searchUrl, requestParams, invalidateCount]
    );

    // handle page change
    const handlePageChange = (page: number) => {
        setPage(page);
    };

    // reset page on param change
    useEffect(() => {
        setPage(1);
    }, [filterValues]);

    const onInvalidate = () => {
        // child components can trigger invalidateCount state update,
        // which will invalidate the fetchModuleData result
        setInvalidateCount((prev) => prev + 1);
    };

    // render with Suspense
    return (
        <ErrorBoundary fallback={<SearchResultsSkeletonComponent />}>
            <Suspense fallback={<SearchResultsSkeletonComponent />}>
                {/* @ts-expect-error (dynamic prop types for displayComponentProps) */}
                <SearchResultsDisplayComponent
                    isSearch={isSearchFilterValues(filterValues)}
                    isTextSearch={!!filterValues?.keyword}
                    fetchModuleData={fetchModuleData}
                    page={page}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                    onEntryShowMore={onEntryShowMore}
                    isWideLayout={isWideLayout}
                    searchInLabel={searchInLabel}
                    bulkButtonActions={bulkButtonActions}
                    bulkMenuActions={bulkMenuActions}
                    onInvalidate={onInvalidate}
                    filterValues={filterValues}
                    areSearchResultsDraggable={areSearchResultsDraggable}
                    {...displayComponentProps}
                />
            </Suspense>
        </ErrorBoundary>
    );
}

export default SearchResultsContainer;
